import type {
  RetentionDispositionAction,
  RetentionDocumentType,
  RetentionPolicy,
  RetentionStatus,
  RetentionUnit,
} from '../../../../domain/src/lib/retention/retention.model';

export interface RetentionPolicyDetailField {
  label: string;
  value: string;
}

const RETENTION_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** ISO/`YYYY-MM-DD` del servidor → Date local para el datepicker (sin hora). */
export function parseRetentionDateForPicker(
  value: string | Date | null | undefined,
): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const datePart = (value ?? '').trim().slice(0, 10);
  if (!RETENTION_ISO_DATE.test(datePart)) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const parsed = new Date(y, m - 1, d);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Datepicker → `YYYY-MM-DD` para el contrato del backend de retención. */
export function toRetentionPolicyStartDateApi(
  value: Date | string | null | undefined,
): string | undefined {
  const parsed = parseRetentionDateForPicker(value);
  if (!parsed) return undefined;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function toRetentionDocumentTypeLabel(type: RetentionDocumentType): string {
  if (type === 'hr_document') return 'Documentos RRHH';
  return 'Documentos de ingesta';
}

export function toRetentionDispositionLabel(action: RetentionDispositionAction): string {
  if (action === 'delete') return 'Eliminar';
  if (action === 'archive') return 'Archivar';
  return 'Revisión';
}

export function toRetentionUnitLabel(unit: RetentionUnit): string {
  if (unit === 'days') return 'día(s)';
  if (unit === 'months') return 'mes(es)';
  return 'año(s)';
}

export function formatRetentionPolicyDate(value: string | null | undefined): string {
  if (!value?.trim()) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function buildRetentionPolicyDetailFields(
  policy: Pick<
    RetentionPolicy,
    | 'id'
    | 'name'
    | 'description'
    | 'document_type'
    | 'document_category'
    | 'retention_period'
    | 'retention_unit'
    | 'disposition_action'
    | 'notify_days_before'
    | 'is_active'
    | 'created_at'
    | 'updated_at'
  >,
): RetentionPolicyDetailField[] {
  const notify =
    policy.notify_days_before == null || policy.notify_days_before < 0
      ? '—'
      : String(policy.notify_days_before);
  return [
    { label: 'Nombre', value: policy.name?.trim() || '—' },
    { label: 'Descripción', value: policy.description?.trim() || '—' },
    { label: 'Tipo de documento', value: toRetentionDocumentTypeLabel(policy.document_type) },
    {
      label: 'Categoría',
      value: policy.document_category?.trim() || '—',
    },
    {
      label: 'Periodo de retención',
      value: `${policy.retention_period} ${toRetentionUnitLabel(policy.retention_unit)}`,
    },
    { label: 'Disposición final', value: toRetentionDispositionLabel(policy.disposition_action) },
    { label: 'Aviso previo (días)', value: notify },
    { label: 'Estado', value: policy.is_active ? 'Activa' : 'Inactiva' },
    { label: 'ID', value: policy.id || '—' },
    { label: 'Creada', value: formatRetentionPolicyDate(policy.created_at) },
    { label: 'Actualizada', value: formatRetentionPolicyDate(policy.updated_at) },
  ];
}

export function toRetentionStatusLabel(status: RetentionStatus): string {
  if (status === 'active') return 'Activa';
  if (status === 'expired') return 'Expirada';
  if (status === 'suspended') return 'Suspendida';
  return 'Dispuesta';
}

export function toRetentionStatusVariant(
  status: RetentionStatus,
): 'info' | 'warning' | 'error' | 'success' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'expired') return 'warning';
  if (status === 'suspended') return 'error';
  return 'neutral';
}
